import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const PROCUREMENT_STAT_CARDS = [
  {
    key: "totalPR",
    label: "Total PR",
    titleColor: "#1F4E79",
    valueColor: "#2874A6",
    filterValue: null,
  },
  {
    key: "submitted",
    label: "Submitted",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterValue: "Submitted",
  },
  {
    key: "pending",
    label: "Pending",
    titleColor: "#9A7D0A",
    valueColor: "#F39C12",
    filterValue: "Pending",
  },
  {
    key: "rfqInProgress",
    label: "RFQ In Progress",
    titleColor: "#1A5276",
    valueColor: "#3498DB",
    filterValue: "RFQ In Progress",
  },
  {
    key: "quoteInProgress",
    label: "Quote In Progress",
    titleColor: "#7D6608",
    valueColor: "#D4AC0D",
    filterValue: "Quote In Progress",
  },
  {
    key: "poCreated",
    label: "PO Created",
    titleColor: "#0B5345",
    valueColor: "#16A085",
    filterValue: "Po created",
  },
  {
    key: "poPartiallyCreated",
    label: "PO Partially Created",
    titleColor: "#B45309",
    valueColor: "#D97706",
    filterValue: "Po Partially created",
  },
  {
    key: "draft",
    label: "Draft",
    titleColor: "#424949",
    valueColor: "#7F8C8D",
    filterValue: "Draft",
  },
  {
    key: "hold",
    label: "Hold",
    titleColor: "#6C3483",
    valueColor: "#8E44AD",
    filterValue: "Hold",
  },
  {
    key: "regret",
    label: "Regret",
    titleColor: "#be0c71",
    valueColor: "#f3329f",
    filterValue: "Regret",
  },
  {
    key: "notQuoted",
    label: "Not Quoted",
    titleColor: "#922B21",
    valueColor: "#E74C3C",
    filterValue: "Not Quoted - RFQ Closed",
  },
];

const DEFAULT_PR_STATS = PROCUREMENT_STAT_CARDS.reduce(
  (acc, card) => ({ ...acc, [card.key]: 0 }),
  {},
);

function normalizePrStats(data) {
  if (!data || typeof data !== "object") return { ...DEFAULT_PR_STATS };
  return PROCUREMENT_STAT_CARDS.reduce(
    (acc, card) => ({
      ...acc,
      [card.key]: Number(data[card.key]) || 0,
    }),
    {},
  );
}

const ProcurementStatCardSkeleton = () => (
  <div className="procurement-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const ProcurementStatCard = ({
  label,
  value,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => (
  <div className="procurement-stat-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable${
        isActive ? " procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      title={`Filter by ${label}`}
    >
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor }}>
          {label}
        </div>
        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

/** Resolve base64 xlsx from export / download API (Gateway body, nested JSON). */
function extractExcelBase64(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("UEs")) return raw;
    if (t.startsWith("{")) {
      try {
        return extractExcelBase64(JSON.parse(t));
      } catch {
        return null;
      }
    }
    return raw;
  }
  if (typeof raw === "object") {
    if (raw.body != null) return extractExcelBase64(raw.body);
    if (typeof raw.data === "string") return extractExcelBase64(raw.data);
    if (raw.data != null && typeof raw.data === "object") {
      const nested = extractExcelBase64(raw.data);
      if (nested) return nested;
    }
  }
  return null;
}

function unwrapApiBody(raw) {
  let responseData = raw;
  if (typeof responseData === "string") {
    try {
      responseData = JSON.parse(responseData);
    } catch {
      return raw;
    }
  }
  if (responseData?.body != null) {
    if (typeof responseData.body === "string") {
      try {
        return JSON.parse(responseData.body);
      } catch {
        return responseData.body;
      }
    }
    return responseData.body;
  }
  return responseData;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractProcurementsResponse(raw) {
  const responseData = unwrapApiBody(raw);
  if (!isApiSuccess(responseData)) return null;

  let dataBlock = responseData.data ?? responseData;
  if (typeof dataBlock === "string") {
    try {
      dataBlock = JSON.parse(dataBlock);
    } catch {
      return null;
    }
  }

  const procurements = dataBlock?.procurements ?? responseData.procurements;
  if (!Array.isArray(procurements)) return null;

  const pagination = dataBlock?.pagination ?? responseData.pagination;
  return {
    procurements,
    pagination,
    message: responseData.message,
  };
}

const PROCUREMENT_STATUS_FILTER_OPTIONS = PROCUREMENT_STAT_CARDS.filter(
  (card) => card.filterValue,
).map((card) => ({
  value: card.filterValue,
  label: card.label,
}));

function normalizeProcurementClientReferences(clients) {
  if (!Array.isArray(clients)) return [];
  return clients
    .map((client) => {
      const clientReference = String(
        client.clientReference || client.clientName || client.name || "",
      ).trim();
      return { clientReference };
    })
    .filter((client) => client.clientReference);
}

function applyProcurementClientReferenceFilter(payload, filterValue) {
  const clientReference = String(filterValue || "").trim();
  if (!clientReference) return payload;
  payload.clientReference = clientReference;
  return payload;
}

const EXPORT_POLL_MS = 2000;
const EXPORT_POLL_MAX_ATTEMPTS = 180; // ~6 min at 2s
const DEFAULT_EXPORT_DAYS_BACK = 30;

function toISODate(date) {
  return date.toISOString().slice(0, 10);
}

function shiftISODate(isoDate, deltaDays) {
  if (!isoDate) return "";
  const dt = new Date(`${isoDate}T00:00:00`);
  dt.setDate(dt.getDate() + deltaDays);
  return toISODate(dt);
}

function toApiDDMMYYYY(isoDate) {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}-${mm}-${yyyy}`;
}

function triggerXlsxDownloadFromBase64(base64) {
  if (!base64 || typeof base64 !== "string" || !base64.startsWith("UEs")) {
    throw new Error("Invalid Excel data received");
  }
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
  link.download = `procurement-export-${timestamp}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

const DEFAULT_FILTER_CHECKS = {
  prNo: false,
  user: false,
  clientReference: false,
  status: false,
  dateRange: false,
  duedate: false,
};
const DEFAULT_FILTER_INPUTS = {
  prNo: "",
  user: "",
  clientReference: "",
  status: "",
  fromDate: "",
  toDate: "",
  duedate: "",
};
const DEFAULT_ACTIVE_MULTI_FILTERS = {
  prNo: "",
  user: "",
  clientReference: "",
  status: "",
  fromDate: "",
  toDate: "",
  duedate: "",
};

const ProcurementTracker = () => {
  const { canCreate, canView, canUpdate, canDelete } = useModuleAccess(
    "procurement_tracker",
  );
  const savedFilters = JSON.parse(
    sessionStorage.getItem("procurementFilters") || "{}",
  );
  const navigate = useNavigate();
  const [procurements, setProcurements] = useState([]);
  const [filteredProcurements, setFilteredProcurements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteProcurementId, setDeleteProcurementId] = useState(null);
  const [deleteProcurementName, setDeleteProcurementName] = useState("");
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [activeFilterType, setActiveFilterType] = useState(
    savedFilters.activeFilterType || "",
  );
  const [activeFilterValue, setActiveFilterValue] = useState(
    savedFilters.activeFilterValue || "",
  );

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterChecks, setFilterChecks] = useState({
    ...DEFAULT_FILTER_CHECKS,
    ...(savedFilters.filterChecks || {}),
  });
  const [filterInputs, setFilterInputs] = useState({
    ...DEFAULT_FILTER_INPUTS,
    ...(savedFilters.filterInputs || {}),
  });
  const [activeMultiFilters, setActiveMultiFilters] = useState({
    ...DEFAULT_ACTIVE_MULTI_FILTERS,
    ...(savedFilters.activeMultiFilters || {}),
  });
  const filterPanelRef = useRef(null);

  const [isExporting, setIsExporting] = useState(false); // Export loading state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState("date");
  const [exportFromDate, setExportFromDate] = useState(
    shiftISODate(toISODate(new Date()), -(DEFAULT_EXPORT_DAYS_BACK - 1)),
  );
  const [exportToDate, setExportToDate] = useState(toISODate(new Date()));
  const [allUsers, setAllUsers] = useState([]);
  const [procurementClientReferences, setProcurementClientReferences] =
    useState([]);
  const [
    isProcurementClientReferencesLoading,
    setIsProcurementClientReferencesLoading,
  ] = useState(false);

  // Modal state for description
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");
  const [selectedPrNo, setSelectedPrNo] = useState("");
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [isPrCountLoading, setIsPrCountLoading] = useState(true);

  const [pr, setPr] = useState({ ...DEFAULT_PR_STATS });

  const anyMultiFilterActive =
    !!activeMultiFilters.prNo ||
    !!activeMultiFilters.user ||
    !!activeMultiFilters.clientReference ||
    !!activeMultiFilters.status ||
    !!activeMultiFilters.fromDate ||
    !!activeMultiFilters.toDate ||
    !!activeMultiFilters.duedate;

  const anyFilterActive = anyMultiFilterActive;
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const buildMultiFilterFields = useCallback(
    (fields = {}) => {
      if (activeMultiFilters.prNo) fields.prNo = activeMultiFilters.prNo;
      if (activeMultiFilters.user) fields.userId = activeMultiFilters.user;
      if (activeMultiFilters.clientReference) {
        applyProcurementClientReferenceFilter(
          fields,
          activeMultiFilters.clientReference,
        );
      }
      if (activeMultiFilters.status)
        fields.quotationStatus = activeMultiFilters.status;
      if (activeMultiFilters.fromDate) {
        fields.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
      }
      if (activeMultiFilters.toDate) {
        fields.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
      }
      if (activeMultiFilters.duedate) {
        fields.duedate = toApiDDMMYYYY(activeMultiFilters.duedate);
      }
      return fields;
    },
    [activeMultiFilters],
  );

  // Fetch procurements with server-side pagination
  const fetchProcurements = useCallback(async () => {
    setIsLoading(true);

    try {
      const payload = {
        action: "get-all-procurements",
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        status: "Active",
      };

      // Add search parameter if activeSearchQuery has a value (not empty)
      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      buildMultiFilterFields(payload);

      // Add sorting parameters if sortBy is set
      if (sortBy) {
        payload.sortBy = sortBy;
        payload.sortOrder = sortOrder;
      }

      const response = await axios.post(API_URL, payload);
      const parsed = extractProcurementsResponse(response.data);

      if (parsed) {
        setProcurements(parsed.procurements);
        setFilteredProcurements(parsed.procurements);

        // Extract and store unique users only when NOT filtering by user
        // This preserves the full user list for the dropdown
        if (!activeMultiFilters.user) {
          const uniqueUsersMap = new Map();
          parsed.procurements.forEach((procurement) => {
            if (
              procurement.userId &&
              procurement.userId._id &&
              procurement.userId.name
            ) {
              uniqueUsersMap.set(procurement.userId._id, {
                _id: procurement.userId._id,
                name: procurement.userId.name,
              });
            }
          });
          setAllUsers((prev) => {
            const merged = new Map(prev.map((user) => [user._id, user]));
            uniqueUsersMap.forEach((user, id) => merged.set(id, user));
            return Array.from(merged.values()).sort((a, b) =>
              a.name.localeCompare(b.name),
            );
          });
        }

        if (parsed.pagination && parsed.pagination.total != null) {
          setTotalCount(parsed.pagination.total);
        } else {
          setTotalCount(parsed.procurements.length);
        }
      } else {
        const responseData = unwrapApiBody(response.data);
        toast.error(
          responseData?.message || "Failed to fetch procurement entries",
          { autoClose: 2000 },
        );
        setProcurements([]);
        setFilteredProcurements([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch procurements error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message ||
            "An error occurred while fetching procurement entries",
          { autoClose: 2000 },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
      setProcurements([]);
      setFilteredProcurements([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    sortBy,
    sortOrder,
    activeMultiFilters,
    buildMultiFilterFields,
  ]);

  useEffect(() => {
    fetchProcurements();
  }, [fetchProcurements]);

  useEffect(() => {
    if (!filterChecks.clientReference) return;
    fetchProcurementClientReferences();
  }, [filterChecks.clientReference]);

  const fetchProcurementClientReferences = async () => {
    setIsProcurementClientReferencesLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-procurement-client-reference",
      });

      let data = unwrapApiBody(response.data);
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          data = response.data;
        }
      }

      if (data?.success && Array.isArray(data.data)) {
        const clients = normalizeProcurementClientReferences(data.data);
        clients.sort((a, b) =>
          a.clientReference.localeCompare(b.clientReference),
        );
        setProcurementClientReferences(clients);
      }
    } catch (error) {
      console.error("Error fetching procurement client references:", error);
    } finally {
      setIsProcurementClientReferencesLoading(false);
    }
  };

  const fetchProcurementStatusCount = useCallback(async () => {
    try {
      setIsPrCountLoading(true);

      const payload = {
        action: "procurement-count",
      };

      buildMultiFilterFields(payload);

      const response = await axios.post(API_URL, payload);

      let data = response.data;

      if (typeof data === "string") {
        data = JSON.parse(data);
      }

      if (data.success) {
        setPr(normalizePrStats(data.data));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsPrCountLoading(false);
    }
  }, [buildMultiFilterFields]);

  useEffect(() => {
    fetchProcurementStatusCount();
  }, [fetchProcurementStatusCount]);

  const fetchProcurementUsers = async () => {
    try {
      const response = await axios.post(API_URL, {
        action: "get-procurement-users",
      });

      const data = unwrapApiBody(response.data);

      if (data.success) {
        setAllUsers(data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProcurementUsers();
  }, []);

  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem =
    procurements.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Show up to 7 page numbers

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages with ellipsis
      if (currentPage <= 4) {
        // Show first pages
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show last pages
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show middle pages
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Handle search input change - if field becomes empty, automatically reset search
  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    // If user backspaces and field becomes empty, automatically reset search to show all records
    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      // Field is now empty and there was an active search, so reset it
      setActiveSearchQuery("");
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle search trigger - on Enter key or search icon click
  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";

    // If search query is empty (user backspaced all characters), reset the active search
    // This will trigger API call without search parameter to show all records
    if (trimmedQuery === "") {
      // Clear the active search to reset filter
      setActiveSearchQuery("");
      // Reset to first page
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    } else {
      // Set the active search with trimmed query
      setActiveSearchQuery(trimmedQuery);
      // Reset to first page when searching
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };


  const MAX_ACTIVE_FILTERS = 2; 

  const handleFilterCheckToggle = (key) => {
    setFilterChecks((prev) => {
      const isCurrentlyChecked = prev[key];

      if (!isCurrentlyChecked) {
        const activeCount = Object.values(prev).filter(Boolean).length;
        if (activeCount >= MAX_ACTIVE_FILTERS) {
          toast.error(
            `You can select up to ${MAX_ACTIVE_FILTERS} filters only`,
            {
              autoClose: 2000,
            },
          );
          return prev;
        }
      }

      const next = { ...prev, [key]: !isCurrentlyChecked };

      if (!next[key]) {
        if (key === "dateRange") {
          setFilterInputs((inputs) => ({
            ...inputs,
            fromDate: "",
            toDate: "",
          }));
          setActiveMultiFilters((active) => ({
            ...active,
            fromDate: "",
            toDate: "",
          }));
        } else {
          setFilterInputs((inputs) => ({ ...inputs, [key]: "" }));
          setActiveMultiFilters((active) => ({ ...active, [key]: "" }));
        }
        if (currentPage !== 1) {
          setCurrentPage(1);
        }
      }

      return next;
    });
  };

  const handleFilterInputChange = (key, value) => {
    if (key === "fromDate" || key === "toDate") {
      const nextFrom = key === "fromDate" ? value : filterInputs.fromDate;
      const nextTo = key === "toDate" ? value : filterInputs.toDate;

      if (
        nextFrom &&
        nextTo &&
        new Date(nextFrom) > new Date(nextTo)
      ) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }

      setFilterInputs((prev) => ({ ...prev, [key]: value }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        fromDate: nextFrom,
        toDate: nextTo,
      }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    setFilterInputs((prev) => ({ ...prev, [key]: value }));
    if (
      key === "user" ||
      key === "clientReference" ||
      key === "status" ||
      key === "duedate"
    ) {
      setActiveMultiFilters((prev) => ({ ...prev, [key]: value }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  const applyPrNoFilter = () => {
    const trimmed = (filterInputs.prNo || "").trim();
    setActiveMultiFilters((prev) => ({ ...prev, prNo: trimmed }));
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handlePrNoKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applyPrNoFilter();
    }
  };

  const handleClearFilter = () => {
    setFilterChecks(DEFAULT_FILTER_CHECKS);
    setFilterInputs(DEFAULT_FILTER_INPUTS);
    setActiveMultiFilters(DEFAULT_ACTIVE_MULTI_FILTERS);
    setActiveFilterType("");
    setActiveFilterValue("");
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const isStatCardActive = (card) => {
    if (!card.filterValue) {
      return !activeMultiFilters.status;
    }
    return activeMultiFilters.status === card.filterValue;
  };

  const handleStatCardClick = (card) => {
    if (!card.filterValue) {
      setFilterChecks((prev) => ({ ...prev, status: false }));
      setFilterInputs((prev) => ({ ...prev, status: "" }));
      setActiveMultiFilters((prev) => ({ ...prev, status: "" }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    if (!filterChecks.status) {
      const otherActiveKeys = Object.keys(filterChecks).filter(
        (key) => key !== "status" && filterChecks[key],
      );

      if (otherActiveKeys.length >= MAX_ACTIVE_FILTERS) {
        const keyToRemove = otherActiveKeys[0];
        setFilterChecks((prev) => ({
          ...prev,
          [keyToRemove]: false,
          status: true,
        }));
        setFilterInputs((prev) => ({
          ...prev,
          [keyToRemove]: "",
          status: card.filterValue,
        }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          [keyToRemove]: "",
          status: card.filterValue,
        }));
        toast.info("Replaced an existing filter to apply Status", {
          autoClose: 2000,
        });
      } else {
        setFilterChecks((prev) => ({ ...prev, status: true }));
        setFilterInputs((prev) => ({ ...prev, status: card.filterValue }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          status: card.filterValue,
        }));
      }
    } else {
      setFilterInputs((prev) => ({ ...prev, status: card.filterValue }));
      setActiveMultiFilters((prev) => ({ ...prev, status: card.filterValue }));
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      currentPage,
      itemsPerPage,
      filterChecks,
      filterInputs,
      activeMultiFilters,
      activeFilterType,
      activeFilterValue,
    };

    sessionStorage.setItem("procurementFilters", JSON.stringify(filters));
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    filterChecks,
    filterInputs,
    activeMultiFilters,
    activeFilterType,
    activeFilterValue,
  ]);

  const handleExport = async () => {
    if (exportMode === "date") {
      if (!exportFromDate || !exportToDate) {
        toast.error("Please select both from date and to date", {
          autoClose: 2000,
        });
        return;
      }
      if (new Date(exportFromDate) > new Date(exportToDate)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }
    }

    setIsExporting(true);
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const buildExportFilterFields = () => {
      const fields = { status: "Active" };
      const trimmedSearch = activeSearchQuery?.trim();
      if (trimmedSearch) {
        fields.search = trimmedSearch;
      }
      buildMultiFilterFields(fields);
      if (activeFilterType === "status" && activeFilterValue) {
        fields.quotationStatus = activeFilterValue;
      }
      return fields;
    };

    const runSyncExport = async () => {
      const payload = {
        action: "export-procurements",
        ...buildExportFilterFields(),
      };
      if (exportMode === "date") {
        payload.fromDate = toApiDDMMYYYY(exportFromDate);
        payload.toDate = toApiDDMMYYYY(exportToDate);
      }
      const response = await axios.post(API_URL, payload, {
        responseType: "json",
      });
      let base64 = extractExcelBase64(unwrapApiBody(response.data));
      if (!base64 && response.data?.body) {
        base64 =
          typeof response.data.body === "string" &&
          response.data.body.trim().startsWith("UEs")
            ? response.data.body
            : extractExcelBase64(response.data.body);
      }
      if (!base64) {
        base64 = extractExcelBase64(response.data);
      }
      triggerXlsxDownloadFromBase64(base64);
    };

    try {
      const startPayload = {
        action: "start-export-procurements",
        ...buildExportFilterFields(),
      };
      if (exportMode === "date") {
        startPayload.fromDate = toApiDDMMYYYY(exportFromDate);
        startPayload.toDate = toApiDDMMYYYY(exportToDate);
      }

      const startRes = await axios.post(API_URL, startPayload, {
        responseType: "json",
        validateStatus: () => true,
      });

      const rawStart = startRes.data;
      const startData = unwrapApiBody(rawStart);
      const jobId =
        startData?.jobId ||
        (typeof startData?.data === "object" && startData.data?.jobId) ||
        rawStart?.jobId ||
        (typeof rawStart?.data === "object" && rawStart.data?.jobId);

      const msgLower = (startData?.message || "").toString().toLowerCase();
      const shouldFallbackSync =
        startRes.status === 400 ||
        (startData?.success === false &&
          (msgLower.includes("export-procurements") ||
            msgLower.includes("lambda") ||
            msgLower.includes("local")));

      const useAsyncJob = !shouldFallbackSync && !!jobId;

      if (shouldFallbackSync || !useAsyncJob) {
        await runSyncExport();
        toast.success("Export completed successfully", { autoClose: 2000 });
        return;
      }

      toast.info("Export started...", { autoClose: 4000 });

      let status = "";
      let lastJob = null;
      for (let attempt = 0; attempt < EXPORT_POLL_MAX_ATTEMPTS; attempt++) {
        if (attempt > 0) {
          await sleep(EXPORT_POLL_MS);
        }
        const pollRes = await axios.post(
          API_URL,
          { action: "get-procurement-export-job", jobId },
          { responseType: "json", validateStatus: () => true },
        );
        lastJob = unwrapApiBody(pollRes.data);
        status =
          lastJob?.status ||
          (lastJob?.data &&
            typeof lastJob.data === "object" &&
            lastJob.data.status) ||
          "";

        if (status === "complete") {
          break;
        }
        if (status === "failed") {
          const errMsg =
            lastJob?.errorMessage ||
            lastJob?.message ||
            (lastJob?.data && lastJob.data.errorMessage) ||
            "Export failed";
          throw new Error(errMsg);
        }
      }

      if (status !== "complete") {
        throw new Error(
          "Export is still processing or timed out. Wait and try again, or narrow filters.",
        );
      }

      const dlRes = await axios.post(
        API_URL,
        { action: "download-procurement-export-job", jobId },
        { responseType: "json", validateStatus: () => true },
      );

      if (dlRes.status === 409) {
        const j = unwrapApiBody(dlRes.data);
        throw new Error(j?.message || "Export file is not ready yet.");
      }
      if (dlRes.status >= 400) {
        const j = unwrapApiBody(dlRes.data);
        throw new Error(j?.message || "Download failed");
      }

      const dlData = unwrapApiBody(dlRes.data);
      const base64 = extractExcelBase64(dlData);
      triggerXlsxDownloadFromBase64(base64);
      toast.success("Export completed successfully", { autoClose: 2000 });
      setShowExportModal(false);
    } catch (error) {
      console.error("Export error:", error);
      const msg =
        typeof error?.message === "string" && error.message
          ? error.message
          : "Failed to export data. Please try again.";
      toast.error(msg, { autoClose: 3000 });
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenExportModal = () => {
    const today = toISODate(new Date());
    const from = shiftISODate(today, -(DEFAULT_EXPORT_DAYS_BACK - 1));
    setExportMode("date");
    setExportFromDate(from);
    setExportToDate(today);
    setShowExportModal(true);
  };

  // Handle column sorting
  const handleSort = (column) => {
    const columnMap = {
      sno: "_id",
      prNo: "prNo",
      prDate: "prDate",
      dueDate: "dueDate",
      clientReference: "clientReference",
      deliveryLocation: "deliveryLocation",
      quantity: "quantity",
      userName: "userName",
      quotationStatus: "quotationStatus",
      rfqOwnerName: "rfqOwnerName",
    };

    const apiField = columnMap[column] || column;

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("createdAt");
        setSortOrder("desc");
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const columnMap = {
      prNo: "prNo",
      prDate: "prDate",
      dueDate: "dueDate",
      clientReference: "clientReference",
      deliveryLocation: "deliveryLocation",
      quantity: "quantity",
      userName: "userName",
      quotationStatus: "quotationStatus",
      createdAt: "createdAt",
    };

    const apiField = columnMap[column] || column;

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em" }}
        />
      );
    }
    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em" }}
        />
      );
    }
    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em" }}
      />
    );
  };

  // Handle delete button click
  const handleDeleteClick = (procurementId, prNo) => {
    setDeleteProcurementId(procurementId);
    setDeleteProcurementName(prNo);
    // Bootstrap modal will be triggered by data-toggle attribute
  };

  // Handle delete confirmation
  const handleDelete = async () => {
    if (!deleteProcurementId) {
      return;
    }

    // Get currentUserId from localStorage
    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return;
    }

    setIsDeleting(true);

    try {
      const payload = {
        action: "delete-procurement",
        id: deleteProcurementId,
        requestUserId: currentUserId,
      };

      const response = await axios.post(API_URL, payload);

      // Parse the response - handle both string and object responses
      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      // Check if deletion was successful
      if (responseData.success === true) {
        toast.success(" Deleted successfully", { autoClose: 2000 });

        // Close modal
        if (window.$) {
          window.$("#modaldelete").modal("hide");
        }

        // Reset delete state
        setDeleteProcurementId(null);
        setDeleteProcurementName("");

        // Refresh procurement list
        // If current page becomes empty after deletion, go to previous page
        if (procurements.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchProcurements();
        }
      } else {
        toast.error(
          responseData.message || "Failed to delete procurement entry",
          { autoClose: 2000 },
        );
      }
    } catch (error) {
      console.error("Delete procurement error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message ||
            "An error occurred while deleting procurement entry",
          { autoClose: 2000 },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddProcurementClick = () => {
    if (window.$) {
      window.$("#modaladdprocurement").modal("show");
    }
  };

  const handleConfirmAddProcurement = async () => {
    if (window.$) {
      window.$("#modaladdprocurement").modal("hide");
    }
    await handleAddProcurement();
  };

  const handleAddProcurement = async () => {
    if (isCreatingDraft) return;

    const userId = localStorage.getItem("id");
    if (!userId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      navigate("/");
      return;
    }

    setIsCreatingDraft(true);
    try {
      const response = await axios.post(API_URL, {
        action: "create-draft-procurement",
        userId,
        createdBy: userId,
        prSummary: "",
        clientReference: "",
        clientReferenceId: null,
        deliveryLocation: "",
        dueDate: "",
        materialCode: "",
        materialDescription: "",
        hsnCode: "",
        uom: "",
        quantity: 0,
      });

      const data = unwrapApiBody(response.data);
      const success =
        data?.success === true ||
        data?.success === "true" ||
        data?.success === 1;

      if (success && data?.data) {
        const prNo = data.data.prNo;
        const id = data.data._id || data.data.id;

        if (!prNo || !id) {
          toast.error("Draft created but response is missing PR details", {
            autoClose: 2000,
          });
          return;
        }

        // toast.success(data.message || 'Draft created successfully', { autoClose: 1500 })
        navigate(`/ProcurementTrackerAdd?id=${id}&prNo=${prNo}&isDraft=true`);
        return;
      }

      toast.error(data?.message || "Failed to create draft", {
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Create draft error:", error);
      const errData = error.response?.data
        ? unwrapApiBody(error.response.data)
        : null;
      toast.error(errData?.message || "Unable to create draft procurement", {
        autoClose: 2000,
      });
    } finally {
      setIsCreatingDraft(false);
    }
  };

  // Format date for display (supports ISO and API DD-MM-YYYY / DD/MM/YYYY)
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const str = String(dateString).trim();
    let date = null;

    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      date = new Date(str);
    } else {
      const dmy = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
      if (dmy) {
        const day = Number(dmy[1]);
        const month = Number(dmy[2]);
        const year = Number(dmy[3]);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(str);
      }
    }

    if (!date || Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString();
  };

  // Get quotation status color class
  const getQuotationStatusColor = (status) => {
    if (!status) return "f_orange"; // Default to pending color
    const statusLower = status.toLowerCase();

    switch (statusLower) {
      case "draft":
        return "text-secondary";
      case "submitted":
        return "f_green"; // Green for submitted
      case "pending":
        return "f_orange"; // Orange for pending
      case "regret":
        return "f_red"; // Red for regret
      case "hold":
        return "text-warning"; // Yellow/Warning for hold
      case "rfq in progress":
        return "text-primary";
      case "quote in progress":
        return "text-warning";
      case "not quoted":
      case "not quoted - rfq closed":
        return "f_red";
      default:
        if (statusLower.includes("rfq in progress")) return "text-primary";
        if (statusLower.includes("quote in progress")) return "text-warning";
        if (statusLower.includes("not quoted")) return "f_red";
        return "f_orange"; // Default to pending color
    }
  };

  // Truncate text with character limit
  const truncateText = (text, maxLength = 25) => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Handle description click to show modal
  const handleDescriptionClick = (description, prNo) => {
    setSelectedDescription(description || "No description available");
    setSelectedPrNo(prNo || "");
    setShowDescriptionModal(true);
  };

  // Close description modal
  const handleCloseDescriptionModal = () => {
    setShowDescriptionModal(false);
    setSelectedDescription("");
    setSelectedPrNo("");
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Purchase Request</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {(canUpdate || canCreate) && (
                <Link
                  to="/procurementTrackerImport"
                  className="btn btn-blue btn-sm mr-2"
                >
                  <i className="fa-solid fa-upload mr-1"></i>
                  Import
                </Link>
              )}
              <button
                type="button"
                className="btn btn-success btn-sm mr-2"
                onClick={handleOpenExportModal}
                disabled={isExporting}
              >
                <i
                  className={`fa-solid ${isExporting ? "fa-spinner fa-spin" : "fa-file-export"} mr-1`}
                ></i>
                {isExporting ? "Exporting..." : "Export"}
              </button>
              {canCreate && (
                <button
                  type="button"
                  className="btn btn-blue"
                  onClick={handleAddProcurementClick}
                  disabled={isCreatingDraft}
                >
                  {isCreatingDraft ? "Creating..." : "Add"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="procurement-stat-card-row">
                {isPrCountLoading
                  ? PROCUREMENT_STAT_CARDS.map((card) => (
                      <ProcurementStatCardSkeleton key={card.key} />
                    ))
                  : PROCUREMENT_STAT_CARDS.map((card) => (
                      <ProcurementStatCard
                        key={card.key}
                        label={card.label}
                        value={pr[card.key]}
                        titleColor={card.titleColor}
                        valueColor={card.valueColor}
                        isActive={isStatCardActive(card)}
                        onClick={() => handleStatCardClick(card)}
                      />
                    ))}
              </div>
              <div className="card-body">
                <div className="row align-items-end mb-4 flex-xl-nowrap">
                  <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                    <div className="form-group mb-0">
                      <label
                        className="mb-1"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Search
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search procurements..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                        />
                        <div className="input-group-append">
                          <div
                            className="input-group-text crsr_pntr"
                            onClick={handleSearchTrigger}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa-solid fa-search" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                    <div
                      className="form-group mb-0 position-relative"
                      ref={filterPanelRef}
                    >
                      <label
                        className="mb-1"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Filter
                      </label>

                      <button
                        type="button"
                        className="form-control text-left d-flex justify-content-between align-items-center"
                        onClick={() => setIsFilterPanelOpen((open) => !open)}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {anyMultiFilterActive ? "Filters" : "Select Filters"}
                        </span>

                        <i
                          className={`fa-solid fa-chevron-${
                            isFilterPanelOpen ? "up" : "down"
                          }`}
                          style={{
                            fontSize: "11px",
                            flexShrink: 0,
                            marginLeft: "6px",
                          }}
                        />
                      </button>

                      {isFilterPanelOpen && (
                        <div
                          className="procurement-filter-dropdown"
                          style={{
                            position: "absolute",
                            zIndex: 20,
                            top: "100%",
                            left: 0,
                            marginTop: "4px",
                            background: "#fff",
                            border: "1px solid #ced4da",
                            borderRadius: "4px",
                            padding: "10px 12px",
                            width: "200px",
                            maxWidth: "calc(100vw - 32px)",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                          }}
                        >
                          {[
                            {
                              key: "prNo",
                              id: "filterCheckPrNo",
                              label: "PR NO",
                            },
                            {
                              key: "user",
                              id: "filterCheckUser",
                              label: "User",
                            },
                            {
                              key: "clientReference",
                              id: "filterCheckClient",
                              label: "Client",
                            },
                            {
                              key: "status",
                              id: "filterCheckStatus",
                              label: "Status",
                            },
                            {
                              key: "dateRange",
                              id: "filterCheckDateRange",
                              label: "Date",
                            },
                            {
                              key: "duedate",
                              id: "filterCheckDueDate",
                              label: "Due Date",
                            },
                          ].map((filter) => {
                            const activeCount =
                              Object.values(filterChecks).filter(
                                Boolean,
                              ).length;

                            return (
                              <div
                                className="custom-control custom-checkbox mb-2"
                                key={filter.key}
                              >
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id={filter.id}
                                  checked={filterChecks[filter.key]}
                                  disabled={
                                    !filterChecks[filter.key] &&
                                    activeCount >= MAX_ACTIVE_FILTERS
                                  }
                                  onChange={() =>
                                    handleFilterCheckToggle(filter.key)
                                  }
                                />

                                <label
                                  className="custom-control-label"
                                  htmlFor={filter.id}
                                >
                                  {filter.label}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {filterChecks.prNo && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          PR Number
                        </label>

                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter PR Number"
                            value={filterInputs.prNo}
                            onChange={(e) =>
                              handleFilterInputChange("prNo", e.target.value)
                            }
                            onKeyPress={handlePrNoKeyPress}
                          />

                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={applyPrNoFilter}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {filterChecks.user && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          User
                        </label>

                        <select
                          className="form-control"
                          value={filterInputs.user}
                          onChange={(e) =>
                            handleFilterInputChange("user", e.target.value)
                          }
                        >
                          <option value="">Select User</option>

                          {allUsers.map((user) => (
                            <option key={user._id} value={user._id}>
                              {user.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.clientReference && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Client
                        </label>

                        <select
                          className="form-control"
                          value={filterInputs.clientReference}
                          onChange={(e) =>
                            handleFilterInputChange(
                              "clientReference",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">
                            {isProcurementClientReferencesLoading &&
                            procurementClientReferences.length === 0
                              ? "Loading clients..."
                              : "Select Client"}
                          </option>

                          {procurementClientReferences.map((client, index) => (
                            <option
                              key={`${client.clientReference}-${index}`}
                              value={client.clientReference}
                            >
                              {client.clientReference}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.status && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Status
                        </label>

                        <select
                          className="form-control"
                          value={filterInputs.status}
                          onChange={(e) =>
                            handleFilterInputChange("status", e.target.value)
                          }
                        >
                          <option value="">Select Status</option>

                          {PROCUREMENT_STATUS_FILTER_OPTIONS.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.dateRange && (
                    <>
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            From Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.fromDate || ""}
                            max={filterInputs.toDate || undefined}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "fromDate",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            To Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.toDate || ""}
                            min={filterInputs.fromDate || undefined}
                            onChange={(e) =>
                              handleFilterInputChange("toDate", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {filterChecks.duedate && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Due Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={filterInputs.duedate || ""}
                          onChange={(e) =>
                            handleFilterInputChange("duedate", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  )}

                  {anyFilterActive && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm w-100"
                        onClick={handleClearFilter}
                        style={{
                          minWidth: "75px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <i className="fa-solid fa-times mr-1" />
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : procurements.length === 0 ? (
                <div className="nocontent_div text-center p-4">
                  There are no procurement entries yet.
                  <br />
                  {/* <div className="mt-3">
                    <Link to="/ProcurementTrackerAdd" className="btn btn-blue">Add Procurement</Link>
                  </div> */}
                </div>
              ) : filteredProcurements.length === 0 && activeSearchQuery ? (
                <div className="nocontent_div text-center p-4">
                  No procurement entries found matching your search.
                  <br />
                  <div className="mt-3">
                    <button
                      className="btn btn-blue"
                      onClick={() => {
                        setSearchQuery("");
                        setActiveSearchQuery("");
                        setCurrentPage(1);
                      }}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="procurements"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr style={{ fontSize: "12px" }}>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("sno")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              S.No
                            </span>
                          </th>
                          <th
                            onClick={() => handleSort("prNo")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              PR No {getSortIcon("prNo")}
                            </span>
                          </th>
                          <th
                            onClick={() => handleSort("prDate")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              PR Date {getSortIcon("prDate")}
                            </span>
                          </th>
                          <th
                            onClick={() => handleSort("dueDate")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Due Date {getSortIcon("dueDate")}
                            </span>
                          </th>
                          <th
                            onClick={() => handleSort("clientReference")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Client Reference {getSortIcon("clientReference")}
                            </span>
                          </th>
                          <th
                            onClick={() => handleSort("deliveryLocation")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Location {getSortIcon("deliveryLocation")}
                            </span>
                          </th>
                          <th
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {" "}
                              Material Code
                            </span>
                          </th>
                          <th
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Material Description
                            </span>
                          </th>
                          <th
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              HSN
                            </span>
                          </th>
                          <th
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              UOM
                            </span>
                          </th>
                          <th
                            onClick={() => handleSort("quantity")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Quantity {getSortIcon("quantity")}
                            </span>
                          </th>
                          <th
                            onClick={() => handleSort("userName")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              User {getSortIcon("userName")}
                            </span>
                          </th>
                          <th
                            onClick={() => handleSort("rfqOwnerName")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              RFQ Owner {getSortIcon("rfqOwnerName")}
                            </span>
                          </th>
                          <th
                            onClick={() => handleSort("quotationStatus")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Status {getSortIcon("quotationStatus")}
                            </span>
                          </th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProcurements.map((procurement, index) => (
                          <tr
                            key={procurement._id}
                            style={{ fontSize: "12px" }}
                          >
                            <td style={{ fontSize: "12px" }}>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {procurement.prNo || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {formatDate(procurement.prDate)}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {formatDate(procurement.dueDate)}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {procurement.clientReference || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {procurement.deliveryLocation || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {procurement.materialCode || "-"}
                            </td>
                            <td
                              style={{
                                fontSize: "12px",
                                cursor:
                                  procurement.materialDescription &&
                                  procurement.materialDescription.length > 18
                                    ? "pointer"
                                    : "default",
                              }}
                              onClick={() => {
                                if (
                                  procurement.materialDescription &&
                                  procurement.materialDescription.length > 18
                                ) {
                                  handleDescriptionClick(
                                    procurement.materialDescription,
                                    procurement.prNo,
                                  );
                                }
                              }}
                              title={procurement.materialDescription || "-"}
                            >
                              {truncateText(
                                procurement.materialDescription,
                                18,
                              )}
                              {procurement.materialDescription &&
                                procurement.materialDescription.length > 18 && (
                                  <i
                                    className="fa-solid fa-external-link-alt ml-1"
                                    style={{ fontSize: "10px" }}
                                  ></i>
                                )}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {procurement.hsnCode || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {procurement.uom || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {procurement.quantity || 0}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {procurement.userId?.name || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {String(procurement.rfqOwnerName || "").trim() ||
                                "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              <span
                                className={`fwt_600 ${getQuotationStatusColor(procurement.quotationStatus)}`}
                              >
                                {procurement.quotationStatus || "Pending"}
                              </span>
                            </td>
                            <td style={{ fontSize: "13px" }}>
                              <div className="d-flex justify-content-center align-items-center">
                                {canCreate && (
                                  <Link
                                    to={`/ProcurementTrackerAdd?prNo=${procurement.prNo}&isDraft=true`}
                                    className="crsr_pntr mr-2"
                                    title="Add More"
                                  >
                                    <i className="fa-solid fa-plus" />
                                  </Link>
                                )}
                                {canView && (
                                  <Link
                                    to={`/procurementView/${procurement._id}`}
                                    className="crsr_pntr mr-2"
                                    title="View"
                                  >
                                    <i className="fa-solid fa-eye" />
                                  </Link>
                                )}
                                {canUpdate && (
                                  <Link
                                    to={`/procurementTrackerEdit/${procurement._id}`}
                                    className="crsr_pntr mr-2"
                                    title="Edit"
                                  >
                                    <i className="fa-solid fa-pen" />
                                  </Link>
                                )}
                                {canDelete && (
                                  <button
                                    type="button"
                                    className="crsr_pntr btn btn-link p-0 border-0 align-baseline"
                                    data-toggle="modal"
                                    data-target="#modaldelete"
                                    onClick={() =>
                                      handleDeleteClick(
                                        procurement._id,
                                        procurement.prNo,
                                      )
                                    }
                                    title="Delete"
                                    style={{
                                      color: "var(--blue, #0094a9)",
                                      textDecoration: "none",
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-trash-can"
                                      style={{ color: "inherit" }}
                                      aria-hidden="true"
                                    />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination - Always at bottom right corner with fixed position */}
                  {!isLoading && procurements.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Confirmation Modal */}
      <div
        className="modal fade"
        id="modaladdprocurement"
        tabIndex={-1}
        aria-labelledby="modaladdprocurementLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaladdprocurementLabel">
                Add Purchase Request
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isCreatingDraft}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to add Purchase Request?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isCreatingDraft}
              >
                No
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleConfirmAddProcurement}
                disabled={isCreatingDraft}
              >
                {isCreatingDraft ? "Creating..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div
        className="modal fade"
        id="modaldelete"
        tabIndex={-1}
        aria-labelledby="modaldeleteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteLabel">
                Delete Procurement Entry
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete <br />
                <span className="fwt_600">{deleteProcurementName}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showExportModal ? "show" : ""}`}
        style={{ display: showExportModal ? "block" : "none" }}
        tabIndex={-1}
        aria-labelledby="modalExportProcurementLabel"
        aria-hidden={!showExportModal}
        onClick={() => {
          if (!isExporting) setShowExportModal(false);
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalExportProcurementLabel">
                Export Procurement Data
              </h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={() => {
                  if (!isExporting) setShowExportModal(false);
                }}
                disabled={isExporting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb-3">
                <label className="mb-2 d-block">Export Type</label>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportAllProcurements"
                    name="exportTypeProcurements"
                    className="custom-control-input"
                    value="all"
                    checked={exportMode === "all"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportAllProcurements"
                  >
                    All
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportDateProcurements"
                    name="exportTypeProcurements"
                    className="custom-control-input"
                    value="date"
                    checked={exportMode === "date"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportDateProcurements"
                  >
                    Filter by date
                  </label>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportFromDate}
                    onChange={(e) => setExportFromDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    max={exportToDate || undefined}
                  />
                </div>
                <div className="form-group col-md-6 mb-0">
                  <label className="mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportToDate}
                    onChange={(e) => setExportToDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    min={exportFromDate || undefined}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Material Description Modal */}
      <div
        className={`modal fade ${showDescriptionModal ? "show" : ""}`}
        style={{ display: showDescriptionModal ? "block" : "none" }}
        tabIndex={-1}
        aria-labelledby="descriptionModalLabel"
        aria-hidden={!showDescriptionModal}
        onClick={handleCloseDescriptionModal}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div
              className="modal-header"
              style={{ backgroundColor: "#17a2b8", color: "white" }}
            >
              <h5 className="modal-title" id="descriptionModalLabel">
                <i className="fa-solid fa-file-lines mr-2"></i>
                Material Description {selectedPrNo && `- ${selectedPrNo}`}
              </h5>
              <button
                type="button"
                className="close"
                onClick={handleCloseDescriptionModal}
                aria-label="Close"
                style={{ color: "white" }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div
              className="modal-body"
              style={{ padding: "20px", fontSize: "13px", lineHeight: "1.6" }}
            >
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "15px",
                  borderRadius: "5px",
                  border: "1px solid #dee2e6",
                  wordWrap: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {selectedDescription}
              </div>
            </div>
            {/* <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseDescriptionModal}
              >
                <i className="fa-solid fa-times mr-2"></i>
                Close
              </button>
            </div> */}
          </div>
        </div>
      </div>
      {showExportModal && <div className="modal-backdrop fade show" />}
      {showDescriptionModal && (
        <div
          className="modal-backdrop fade show"
          onClick={handleCloseDescriptionModal}
        ></div>
      )}
    </div>
  );
};

export default ProcurementTracker;
